import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Truck, Briefcase, ArrowRight, MessageCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { generateWhatsAppLink } from '@/utils/whatsappTemplates';
import { TRUST_BADGES } from '@/data/mockData';

const QuoteStart = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get pre-filled parameters from URL
  const service = searchParams.get('service');
  const suburb = searchParams.get('suburb');
  const campaign = searchParams.get('campaign');
  
  const services = [
    {
      id: 'delivery',
      title: 'Delivery',
      icon: Package,
      benefits: [
        'Store pickups & marketplace items',
        'Same-day or scheduled options',
        'WhatsApp updates + proof-of-delivery'
      ],
      path: '/quote/delivery'
    },
    {
      id: 'mini-move',
      title: 'Mini-Move',
      icon: Truck,
      benefits: [
        'Furniture, appliances, boxes',
        'Driver-only or with helpers',
        'Care-first handling with straps & blankets'
      ],
      path: '/quote/mini-move'
    },
    {
      id: 'business',
      title: 'Business Account',
      icon: Briefcase,
      benefits: [
        'Monthly consolidated invoicing',
        'Priority dispatch queue',
        '7-30 day payment terms'
      ],
      path: '/quote/business'
    }
  ];

  const handleServiceSelect = (serviceId) => {
    const params = new URLSearchParams();
    if (suburb) params.set('suburb', suburb);
    if (campaign) params.set('campaign', campaign);
    
    const service = services.find(s => s.id === serviceId);
    navigate(`${service.path}?${params.toString()}`);
  };

  // If service is pre-selected, redirect immediately
  React.useEffect(() => {
    if (service) {
      const foundService = services.find(s => s.id === service);
      if (foundService) {
        handleServiceSelect(service);
      }
    }
  }, [service]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Get a Quote in Minutes
            </h1>
            <p className="text-xl text-gray-300 mb-2">
              Stellenbosch & Cape Town | Fast • Reliable
            </p>
            {suburb && (
              <p className="text-lg text-[#039fff] font-semibold">
                Service for: {suburb}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto">
            {TRUST_BADGES.slice(0, 4).map((badge, index) => {
              const Icon = { MessageCircle, CheckCircle, Package }[badge.icon] || CheckCircle;
              return (
                <div key={index} className="flex items-center space-x-2">
                  <Icon className="h-5 w-5 text-[#039fff]" />
                  <span className="text-sm text-gray-700">{badge.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Selection */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">
              What do you need moved?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service) => {
                const Icon = service.icon;
                return (
                  <Card 
                    key={service.id}
                    className="hover:shadow-xl transition-all cursor-pointer border-2 hover:border-[#039fff]"
                    onClick={() => handleServiceSelect(service.id)}
                  >
                    <CardContent className="p-8">
                      <div className="bg-[#039fff]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
                        <Icon className="h-8 w-8 text-[#039fff]" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#001A47] mb-4">{service.title}</h3>
                      <ul className="space-y-2 mb-6">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-700">
                            <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                      <Button className="w-full bg-[#001A47] hover:bg-[#002B6B] text-white">
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Alternative */}
      <section className="py-12 bg-[#039fff]/5">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-xl font-bold text-[#001A47] mb-4">Prefer to chat?</h3>
          <a href={generateWhatsAppLink('generic', { suburb })} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-[#25D366] hover:bg-[#22c55e] text-white">
              <MessageCircle className="mr-2 h-5 w-5" />
              Get Quote on WhatsApp Instead
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default QuoteStart;
